import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent } from 'react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { useTranslate } from '~/shared/context/use-translate';
import type {
    JobArchitectureItemReference,
    JobPostingReference,
    SkillCatalogTaskReference,
    SkillReferences as SkillReferencesData,
} from '~/shared/data-contract/skill';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { Skeleton } from '~/ui/core/skeleton/Skeleton';

import { jobPageUrl } from '../../app/pages/job-list/constants';
import {
    createJsaProfileDetailTilePayload,
    JSA_PROFILE_DETAIL_TILE_TYPE,
} from '../../job-architecture-v2/url-tile-payloads';
import {
    createWorkTaskTilePayload,
    JSA_WORK_TASK_TILE_TYPE,
} from '../../job-architecture-v2/work-task-tile/work-task-tile-url';
import type { LEARNING_OPPORTUNITY_DETAIL_TILE_TYPE as LearningOpportunityDetailTileType } from '../../learning-opportunities-v2/constants';
import { useOptionalTileAvailability } from '../../url-tiles/availability/tile-availability-context';
import { useOptionalUrlTiles } from '../../url-tiles/use-url-tiles';
import { useSkillReferencesQuery } from './api/use-skill-references';
import type { SkillReferenceRow } from './SkillReferencesList';
import { SkillReferencesList } from './SkillReferencesList';

const ITEM_TYPE_BY_LAYER = { level: 'job-level', role: 'job-role' } as const;
// The learning-opportunity constants module imports the url-tiles barrel, which cycles back through the
// tile definitions into this panel; the literal stays type-checked against the constant without the edge.
const LEARNING_OPPORTUNITY_DETAIL_TILE_TYPE: typeof LearningOpportunityDetailTileType =
    'v1.learning-opportunity.detail';

const SkillReferencesSkeleton: FunctionComponent = () => (
    <div className="flex flex-col gap-m" data-testid="skill-references-loading">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/3" />
    </div>
);

export const SkillReferences: FunctionComponent<{
    readonly className?: string;
    readonly language?: LanguageCodeForJSA;
    readonly skillId: string;
}> = ({ className, language, skillId }) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const urlTiles = useOptionalUrlTiles();
    const tileAvailability = useOptionalTileAvailability();
    const { data, isError, isLoading } = useSkillReferencesQuery(skillId, language);

    const canOpenTileType = useCallback(
        (tileType: string): boolean =>
            !is.null(urlTiles) && (is.null(tileAvailability) || tileAvailability.availabilityByType.has(tileType)),
        [tileAvailability, urlTiles],
    );

    const openProfile = useCallback(
        (item: JobArchitectureItemReference): void => {
            urlTiles?.openTile(
                createJsaProfileDetailTilePayload({
                    architectureId: item.architectureId,
                    itemType: ITEM_TYPE_BY_LAYER[item.layer],
                    mode: 'view',
                    preferredLanguage: language,
                    profileId: item.id,
                }),
            );
        },
        [language, urlTiles],
    );

    const openTask = useCallback(
        (task: SkillCatalogTaskReference): void => {
            urlTiles?.openTile(createWorkTaskTilePayload({ language, taskId: task.id, version: task.version }));
        },
        [language, urlTiles],
    );

    const openLearningOpportunity = useCallback(
        (id: string): void => {
            urlTiles?.openTile({ opportunityId: id, type: LEARNING_OPPORTUNITY_DETAIL_TILE_TYPE });
        },
        [urlTiles],
    );

    const openJobPosting = useCallback(
        (job: JobPostingReference): void => {
            navigate(jobPageUrl(job.id));
        },
        [navigate],
    );

    const rows = useMemo((): readonly SkillReferenceRow[] => {
        if (is.undefined(data)) {
            return [];
        }

        const canOpenProfiles = canOpenTileType(JSA_PROFILE_DETAIL_TILE_TYPE);
        const canOpenTasks = canOpenTileType(JSA_WORK_TASK_TILE_TYPE);
        const canOpenLearningOpportunities = canOpenTileType(LEARNING_OPPORTUNITY_DETAIL_TILE_TYPE);

        const profileRow = (id: 'levels' | 'roles', list: SkillReferencesData['roles']): SkillReferenceRow => ({
            id,
            items: list.items.map((item) => ({
                id: item.id,
                onClick: canOpenProfiles ? () => openProfile(item) : undefined,
                title: item.title,
            })),
            label: translate(`components.skill-references.${id}`),
            totalCount: list.totalCount,
        });

        return [
            profileRow('roles', data.roles),
            profileRow('levels', data.levels),
            {
                id: 'competencies',
                items: data.competencies.items.map(({ id, title }) => ({ id, title })),
                label: translate('components.skill-references.competencies'),
                totalCount: data.competencies.totalCount,
            },
            {
                id: 'tasks',
                items: data.tasks.items.map((task) => ({
                    id: task.id,
                    onClick: canOpenTasks ? () => openTask(task) : undefined,
                    title: task.title,
                })),
                label: translate('components.skill-references.tasks'),
                totalCount: data.tasks.totalCount,
            },
            {
                id: 'learning-opportunities',
                items: data.learningOpportunities.items.map(({ id, title }) => ({
                    id,
                    onClick: canOpenLearningOpportunities ? () => openLearningOpportunity(id) : undefined,
                    title,
                })),
                label: translate('components.skill-references.learning-opportunities'),
                totalCount: data.learningOpportunities.totalCount,
            },
            ...(is.null(data.jobPostings)
                ? []
                : [
                      {
                          id: 'job-postings' as const,
                          items: data.jobPostings.items.map((job) => ({
                              id: job.id,
                              onClick: () => openJobPosting(job),
                              title: job.title,
                          })),
                          label: translate('components.skill-references.job-postings'),
                          totalCount: data.jobPostings.totalCount,
                      },
                  ]),
            ...(is.null(data.employees)
                ? []
                : [
                      {
                          id: 'employees' as const,
                          items: [],
                          label: translate('components.skill-references.employees'),
                          totalCount: data.employees.totalCount,
                      },
                  ]),
        ];
    }, [canOpenTileType, data, openJobPosting, openLearningOpportunity, openProfile, openTask, translate]);

    const title = translate('components.skill-references.title');

    if (isLoading) {
        return (
            <div className={className}>
                <SkillReferencesSkeleton />
            </div>
        );
    }

    if (isError || is.undefined(data)) {
        return (
            <p className={clsx('m-0 text-body-s text-text-muted', className)} data-testid="skill-references-error">
                {translate('components.skill-references.error')}
            </p>
        );
    }

    return (
        <div className={className}>
            <SkillReferencesList rows={rows} testId="skill-references" title={title} />
        </div>
    );
};
